import type { LicenseWarning } from '#layers/license/public';
import type { SingletonToastContent } from '#layers/ui/public';
import { useSessionStorage } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '#layers/access/stores/auth';
import { usePermissionsStore } from '#layers/access/stores/permissions';
import { storageKey, useServerNow, useSystemInfo } from '#layers/base/public';
import { decideLicenseWarning } from '#layers/license/public';
import { useSingletonToast } from '#layers/ui/public';

const DISMISSED_STORAGE_KEY = 'license-expiry-dismissed';
const TOAST_ID = 'license-expiry';

/**
 * Surfaces a single license-expiry warning toast, keeping it in sync with the
 * pure `decideLicenseWarning`. This composable owns license policy only —
 * which warning to show, per-key dismissal, and the copy. The toast lifecycle
 * (add/update/remove timing) belongs to `useSingletonToast`.
 */
export function useLicenseExpiryToast(options: { licensePath: string }) {
	const { t } = useI18n();
	const systemInfo = useSystemInfo();
	const { isOrganizationAdmin, loggedIn } = storeToRefs(useAuth());
	const permissions = usePermissionsStore();

	const canSeeWarning = computed(() => loggedIn.value && (isOrganizationAdmin.value || permissions.isAdmin));

	const now = useServerNow();

	const dismissed = useSessionStorage<string[]>(storageKey(DISMISSED_STORAGE_KEY), []);
	// `useSessionStorage` hands back whatever JSON is in storage; guard against a
	// hostile or stale non-array value before treating it as a string list.
	const dismissedKeys = computed(() => (Array.isArray(dismissed.value) ? dismissed.value : []));

	const warning = computed(() => decideLicenseWarning({
		...systemInfo.license,
		now: now.value,
	}));

	/** Identifies a distinct warning state; a later escalation is a new key. */
	const activeKey = computed(() => (warning.value ? `${warning.value.phase}:${warning.value.severity}` : null));

	const desired = computed<SingletonToastContent | null>(() => {
		if (!canSeeWarning.value) return null;
		const value = warning.value;
		const key = activeKey.value;
		if (!value || key === null || dismissedKeys.value.includes(key)) return null;
		return content(value);
	});

	const toast = useSingletonToast(TOAST_ID, { onUserClose: dismissActiveKey });

	watch(desired, (value) => toast.sync(value), { immediate: true });

	function dismissActiveKey() {
		const key = activeKey.value;
		if (key !== null && !dismissedKeys.value.includes(key)) dismissed.value = [...dismissedKeys.value, key];
	}

	function content(value: LicenseWarning): SingletonToastContent {
		return {
			title: value.phase === 'grace'
				? t('license.expiry.grace_title', { days: value.daysLeft })
				: t('license.expiry.expiring_title', { days: value.daysLeft }),
			description: isOrganizationAdmin.value
				? t('license.expiry.description_org_admin')
				: t('license.expiry.description'),
			// Only org admins can reach the license page; the page redirects everyone else.
			actions: isOrganizationAdmin.value
				? [{
						label: t('license.expiry.view_license'),
						to: options.licensePath,
						color: 'neutral',
						variant: 'outline',
					}]
				: undefined,
			color: value.severity,
			icon: 'lucide:alert-triangle',
			duration: 0,
		};
	}
}
